import { Linking, Platform } from 'react-native';

export type CalendarProvider = 'apple' | 'google' | 'outlook';

export type CalendarEvent = {
  title: string;
  details: string;
  startIso: string;
  endIso: string;
  /**
   * Seed for the .ics UID. RFC 5545 §3.8.4.7 treats identical UIDs as the SAME
   * event (import = update), so this must be unique per user AND per booking —
   * mix in user / coach / consult ids. Falls back to start+title when omitted
   * (which collides across users; pass a real seed wherever two users could add
   * the same consult).
   */
  uid?: string;
};

// Compact UTC stamp (YYYYMMDDTHHMMSSZ) used by Google Calendar + ICS.
function toCompactUtc(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

// Escape an ICS TEXT value per RFC 5545 §3.3.11: backslash, semicolon, comma,
// and newlines are reserved. `details`/`title` are free text (a consult note
// can carry commas), and an unescaped comma is a value-list separator that
// would truncate the field.
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function googleUrl({ title, details, startIso, endIso }: CalendarEvent): string {
  const dates = `${toCompactUtc(startIso)}/${toCompactUtc(endIso)}`;
  return (
    'https://calendar.google.com/calendar/render?action=TEMPLATE' +
    `&text=${encodeURIComponent(title)}&dates=${dates}&details=${encodeURIComponent(details)}`
  );
}

function outlookUrl({ title, details, startIso, endIso }: CalendarEvent): string {
  return (
    'https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent' +
    `&subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(startIso)}` +
    `&enddt=${encodeURIComponent(endIso)}&body=${encodeURIComponent(details)}`
  );
}

// A self-contained .ics body for Apple Calendar (and any .ics handler).
function buildIcs({ title, details, startIso, endIso, uid }: CalendarEvent): string {
  // UID + DTSTAMP are required by RFC 5545; Outlook in particular has refused
  // imports without them. Per §3.8.4.7 identical UIDs are the SAME event, so the
  // seed must be unique per user + booking — callers pass `uid` (user/coach/
  // consult ids). Fall back to start+title only when no seed is given. Normalize
  // to alphanumerics + hyphens so ids carrying ':'/'T' (ISO dates) stay valid.
  const uidSeed = (uid ?? `${toCompactUtc(startIso)}-${title}`).replace(/[^a-zA-Z0-9]+/g, '-');
  const eventUid = `${uidSeed}@vida.com`;
  // Lines MUST be CRLF-delimited (§3.1). Most clients tolerate bare LF, but
  // Apple Calendar has been observed to silently reject LF-only files.
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vida Health//Onboarding//EN',
    'BEGIN:VEVENT',
    `UID:${eventUid}`,
    `DTSTAMP:${toCompactUtc(new Date().toISOString())}`,
    `DTSTART:${toCompactUtc(startIso)}`,
    `DTEND:${toCompactUtc(endIso)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(details)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

function icsDataUri(event: CalendarEvent): string {
  return `data:text/calendar;charset=utf8,${encodeURIComponent(buildIcs(event))}`;
}

export function calendarUrl(provider: CalendarProvider, event: CalendarEvent): string {
  if (provider === 'google') return googleUrl(event);
  if (provider === 'outlook') return outlookUrl(event);
  return icsDataUri(event);
}

// Download the .ics via a Blob object URL + a synthetic <a download> click.
// iOS Safari blocks `window.open('data:text/calendar…')` (a data: URI can't
// open a new tab/window from script), so web users would silently get nothing;
// a blob download lands the file in the downloads tray, which iOS Safari does
// honor. Object URL is revoked on the next tick — after the click is processed.
function downloadIcs(event: CalendarEvent): void {
  const blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'vida-consult.ics';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Opens the provider's add-event flow: a new tab for the Google/Outlook web
 * compose URLs, an .ics download for Apple, or the OS handler on native.
 */
export function openCalendarEvent(provider: CalendarProvider, event: CalendarEvent): void {
  if (Platform.OS === 'web') {
    // Apple has no web compose URL — it's an .ics. Download it rather than
    // window.open(data:…), which iOS Safari blocks.
    if (provider === 'apple') {
      downloadIcs(event);
      return;
    }
    window.open(calendarUrl(provider, event), '_blank');
    return;
  }
  void Linking.openURL(calendarUrl(provider, event)).catch(() => {});
}
